import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export interface BrandConfig {
  name:           string;
  tagline:        string;
  logo_url:       string;
  favicon_url:    string;
  primary_color:  string;   // dominant brand color (e.g. dark green "#1a3c2c")
  accent_color:   string;   // highlight / CTA color (e.g. gold "#f7dc6f")
  bg_color:       string;   // page background
  footer_text:    string;
  support_email:  string;
  login_intro:    string;   // shown below logo on login page
}

export const DEFAULT_BRAND: BrandConfig = {
  name:           "2hives.ai",
  tagline:        "Multi-agent orchestration",
  logo_url:       "/icon.svg",
  favicon_url:    "/icon.svg",
  primary_color:  "#1a3c2c",
  accent_color:   "#f7dc6f",
  bg_color:       "#0f2318",
  footer_text:    "© 2hives.ai",
  support_email:  "info@2hives.ai",
  login_intro:    "Sign in to your 2hives.ai account.",
};

function applyToDom(b: BrandConfig) {
  const r = document.documentElement;
  r.style.setProperty("--brand-primary",  b.primary_color);
  r.style.setProperty("--brand-accent",   b.accent_color);
  r.style.setProperty("--brand-bg",       b.bg_color);
  document.title = b.name;
  const icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (icon) icon.href = b.favicon_url;
}

/** @internal – exported for testing; prefer useBrand() in application code */
export const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND);
const BrandCtx = BrandContext;

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<BrandConfig>(DEFAULT_BRAND);

  useEffect(() => {
    fetch("/brand.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: Partial<BrandConfig> | null) => {
        const merged = data ? { ...DEFAULT_BRAND, ...data } : DEFAULT_BRAND;
        setBrand(merged);
        applyToDom(merged);
      })
      .catch(() => applyToDom(DEFAULT_BRAND));
  }, []);

  return <BrandCtx.Provider value={brand}>{children}</BrandCtx.Provider>;
}

export function useBrand(): BrandConfig {
  return useContext(BrandCtx);
}
